import { useState, useEffect } from 'react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { Settings, Building2, Globe, DollarSign, AlertTriangle, Receipt, Phone, Mail, Star } from 'lucide-react';

export default function SettingsPage() {
  const { user } = useAuth();
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    phone2: '',
    email: '',
    receipt_tagline: '',
    return_policy: '',
    loyalty_points_per_dollar: 100,
    loyalty_point_value: 1,
    loyalty_min_redeem: 500,
    loyalty_expiry_months: 12,
    currency: 'JMD',
    timezone: 'America/Jamaica',
    allow_negative_stock: false
  });

  useEffect(() => {
    fetchOrganization();
  }, []);

  const fetchOrganization = async () => {
    try {
      const response = await api.get('/organization');
      setOrganization(response.data);
      setFormData({
        name: response.data.name || '',
        address: response.data.address || '',
        phone: response.data.phone || '',
        phone2: response.data.phone2 || '',
        email: response.data.email || '',
        receipt_tagline: response.data.receipt_tagline || '',
        return_policy: response.data.return_policy || '',
        loyalty_points_per_dollar: response.data.loyalty_points_per_dollar || 100,
        loyalty_point_value: response.data.loyalty_point_value || 1,
        loyalty_min_redeem: response.data.loyalty_min_redeem || 500,
        loyalty_expiry_months: response.data.loyalty_expiry_months || 12,
        currency: response.data.currency || 'JMD',
        timezone: response.data.timezone || 'America/Jamaica',
        allow_negative_stock: response.data.allow_negative_stock || false
      });
    } catch (error) {
      toast.error('Failed to fetch organization');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put('/organization', formData);
      toast.success('Settings saved');
      fetchOrganization();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const isAdmin = user?.role === 'org_admin' || user?.role === 'super_admin';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#007AFF]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-heading-black text-3xl lg:text-4xl text-[#0A0A0A] tracking-tight">SETTINGS</h1>
        <p className="text-[#0A0A0A]/60 mt-1">Manage organization settings and preferences</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Organization Info */}
        <Card className="border border-[#E5E5EA] shadow-none">
          <CardHeader className="border-b border-[#E5E5EA] pb-4">
            <CardTitle className="text-heading text-lg flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#007AFF]" />
              Organization Information
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="space-y-2">
              <Label>Business Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Your Business Name"
                disabled={!isAdmin}
                data-testid="org-name-input"
              />
              <p className="text-xs text-[#0A0A0A]/40">This appears on receipts and invoices</p>
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="123 Main Street, Portmore, Jamaica"
                disabled={!isAdmin}
                data-testid="org-address-input"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Phone className="w-3.5 h-3.5 text-[#0A0A0A]/50" />
                  Phone Number 1
                </Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="876-450-7828"
                  disabled={!isAdmin}
                  data-testid="org-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Phone className="w-3.5 h-3.5 text-[#0A0A0A]/50" />
                  Phone Number 2
                </Label>
                <Input
                  value={formData.phone2}
                  onChange={(e) => setFormData({ ...formData, phone2: e.target.value })}
                  placeholder="876-377-3479"
                  disabled={!isAdmin}
                  data-testid="org-phone2-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Mail className="w-3.5 h-3.5 text-[#0A0A0A]/50" />
                Email
              </Label>
              <Input
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="info@yourbusiness.com"
                disabled={!isAdmin}
                data-testid="org-email-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Receipt Settings */}
        <Card className="border border-[#E5E5EA] shadow-none">
          <CardHeader className="border-b border-[#E5E5EA] pb-4">
            <CardTitle className="text-heading text-lg flex items-center gap-2">
              <Receipt className="w-5 h-5 text-[#007AFF]" />
              Receipt Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="space-y-2">
              <Label>Receipt Tagline</Label>
              <Input
                value={formData.receipt_tagline}
                onChange={(e) => setFormData({ ...formData, receipt_tagline: e.target.value })}
                placeholder="Thank you for your business!"
                disabled={!isAdmin}
                data-testid="org-tagline-input"
              />
              <p className="text-xs text-[#0A0A0A]/40">Custom message shown at the bottom of receipts</p>
            </div>
            <div className="space-y-2">
              <Label>Return Policy</Label>
              <textarea
                value={formData.return_policy}
                onChange={(e) => setFormData({ ...formData, return_policy: e.target.value })}
                placeholder={"e.g.\n3-day return on Accessories\n5-day return on Screens/Parts\nConditions apply"}
                disabled={!isAdmin}
                rows={3}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="org-return-policy-input"
              />
              <p className="text-xs text-[#0A0A0A]/40">Each line appears as a separate line on the receipt. Leave blank for no return policy.</p>
            </div>
            <div className="bg-[#F5F5F7] p-4 rounded-lg">
              <p className="text-xs text-[#0A0A0A]/50 uppercase tracking-wider mb-2">Receipt Preview</p>
              <div className="bg-white p-4 rounded border border-[#E5E5EA] font-mono text-xs text-center space-y-1">
                <p className="font-bold text-sm">{formData.name || 'Your Business Name'}</p>
                {formData.address && <p className="text-[#0A0A0A]/60">{formData.address}</p>}
                {formData.phone && <p className="text-[#0A0A0A]/60">Tel: {formData.phone}{formData.phone2 ? ` / ${formData.phone2}` : ''}</p>}
                {formData.email && <p className="text-[#0A0A0A]/60">{formData.email}</p>}
                <p className="border-t border-dashed mt-2 pt-2 text-[#0A0A0A]/60">--- Items will appear here ---</p>
                {formData.return_policy && (
                  <div className="border-t border-dashed mt-2 pt-2">
                    <p className="font-bold text-[#0A0A0A]/70 text-[10px] mb-1">RETURN POLICY</p>
                    {formData.return_policy.split('\n').map((line, i) => (
                      <p key={i} className="text-[#0A0A0A]/50">{line}</p>
                    ))}
                  </div>
                )}
                <p className="border-t border-dashed mt-2 pt-2 text-[#0A0A0A]/40">{formData.receipt_tagline || 'Thank you for your purchase!'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Loyalty Program Settings */}
        <Card className="border border-[#E5E5EA] shadow-none">
          <CardHeader className="border-b border-[#E5E5EA] pb-4">
            <CardTitle className="text-heading text-lg flex items-center gap-2">
              <Star className="w-5 h-5 text-[#FFD700]" />
              Loyalty Rewards Program
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Spend Amount per Point (J$)</Label>
                <Input
                  type="number"
                  value={formData.loyalty_points_per_dollar}
                  onChange={(e) => setFormData({ ...formData, loyalty_points_per_dollar: parseInt(e.target.value) || 100 })}
                  disabled={!isAdmin}
                  data-testid="loyalty-points-per-dollar"
                />
                <p className="text-xs text-[#0A0A0A]/40">Customer earns 1 point for every J${formData.loyalty_points_per_dollar} spent</p>
              </div>
              <div className="space-y-2">
                <Label>Point Value (J$)</Label>
                <Input
                  type="number"
                  value={formData.loyalty_point_value}
                  onChange={(e) => setFormData({ ...formData, loyalty_point_value: parseFloat(e.target.value) || 1 })}
                  disabled={!isAdmin}
                  data-testid="loyalty-point-value"
                />
                <p className="text-xs text-[#0A0A0A]/40">1 point = J${formData.loyalty_point_value} store credit</p>
              </div>
              <div className="space-y-2">
                <Label>Minimum Points to Redeem</Label>
                <Input
                  type="number"
                  value={formData.loyalty_min_redeem}
                  onChange={(e) => setFormData({ ...formData, loyalty_min_redeem: parseInt(e.target.value) || 500 })}
                  disabled={!isAdmin}
                  data-testid="loyalty-min-redeem"
                />
                <p className="text-xs text-[#0A0A0A]/40">{formData.loyalty_min_redeem} points = J${(formData.loyalty_min_redeem * formData.loyalty_point_value).toLocaleString()} store credit</p>
              </div>
              <div className="space-y-2">
                <Label>Points Expiry (Months)</Label>
                <Input
                  type="number"
                  value={formData.loyalty_expiry_months}
                  onChange={(e) => setFormData({ ...formData, loyalty_expiry_months: parseInt(e.target.value) || 12 })}
                  disabled={!isAdmin}
                  data-testid="loyalty-expiry"
                />
                <p className="text-xs text-[#0A0A0A]/40">Points expire after {formData.loyalty_expiry_months} months of inactivity</p>
              </div>
            </div>
            <div className="bg-[#FFD700]/5 border border-[#FFD700]/20 rounded-lg p-4">
              <p className="font-semibold text-sm text-[#0A0A0A] mb-2">How It Works (Customer View):</p>
              <div className="space-y-1 text-sm text-[#0A0A0A]/70">
                <p>Spend J${formData.loyalty_points_per_dollar?.toLocaleString()} = Earn 1 Point</p>
                <p>1 Point = J${formData.loyalty_point_value} Store Credit</p>
                <p>{formData.loyalty_min_redeem} Points = J${(formData.loyalty_min_redeem * formData.loyalty_point_value).toLocaleString()} Store Credit</p>
                <p>Points expire after {formData.loyalty_expiry_months} months</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Regional Settings */}
        <Card className="border border-[#E5E5EA] shadow-none">
          <CardHeader className="border-b border-[#E5E5EA] pb-4">
            <CardTitle className="text-heading text-lg flex items-center gap-2">
              <Globe className="w-5 h-5 text-[#007AFF]" />
              Regional Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Currency</Label>
                <Input
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  placeholder="USD"
                  disabled={!isAdmin}
                  data-testid="org-currency-input"
                />
                <p className="text-xs text-[#0A0A0A]/50">ISO 4217 currency code (e.g., USD, EUR, GBP)</p>
              </div>
              <div className="space-y-2">
                <Label>Timezone</Label>
                <Input
                  value={formData.timezone}
                  onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                  placeholder="America/New_York"
                  disabled={!isAdmin}
                  data-testid="org-timezone-input"
                />
                <p className="text-xs text-[#0A0A0A]/50">IANA timezone identifier</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Inventory Settings */}
        <Card className="border border-[#E5E5EA] shadow-none">
          <CardHeader className="border-b border-[#E5E5EA] pb-4">
            <CardTitle className="text-heading text-lg flex items-center gap-2">
              <Settings className="w-5 h-5 text-[#007AFF]" />
              Inventory Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-[#FF9500]" />
                  Allow Negative Stock
                </Label>
                <p className="text-sm text-[#0A0A0A]/50">
                  Allow stock levels to go below zero. Enable only if backorders are supported.
                </p>
              </div>
              <Switch
                checked={formData.allow_negative_stock}
                onCheckedChange={(checked) => setFormData({ ...formData, allow_negative_stock: checked })}
                disabled={!isAdmin}
                data-testid="allow-negative-stock-switch"
              />
            </div>

            <Separator />

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-[#007AFF]" />
                Valuation Method
              </Label>
              <div className="bg-[#F5F5F7] p-4 rounded-sm">
                <p className="font-medium text-[#0A0A0A]">Moving Average Cost (MAC)</p>
                <p className="text-sm text-[#0A0A0A]/60 mt-1">
                  Stock is valued using the weighted average of all purchases. Cost is recalculated on each goods receipt.
                </p>
              </div>
              <p className="text-xs text-[#0A0A0A]/40">FIFO valuation will be available in a future update.</p>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        {isAdmin && (
          <div className="flex justify-end">
            <Button 
              type="submit" 
              className="bg-[#007AFF] hover:bg-[#0066D6]"
              disabled={saving}
              data-testid="save-settings-btn"
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        )}
      </form>

      {/* Current Plan Info */}
      {organization && (
        <Card className="border border-[#E5E5EA] shadow-none">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-overline text-[#0A0A0A]/50 mb-1">CURRENT PLAN</p>
                <p className="text-heading text-xl text-[#0A0A0A]">{organization.subscription_plan || 'Free Trial'}</p>
                {organization.trial_ends_at && (
                  <p className="text-sm text-[#FF9500] mt-1">
                    Trial ends: {new Date(organization.trial_ends_at).toLocaleDateString()}
                  </p>
                )}
              </div>
              <Button variant="outline" onClick={() => window.location.href = '/app/billing'} data-testid="manage-plan-btn">
                Manage Plan
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
